import { FunctionsError } from "firebase/functions";
import { auth } from "../../firebase";
import { notifyAccessMutation } from "../auth/accessCacheEvents";
import { getCentralAccessAppCheckToken } from "./centralAccessAppCheck";
import { coordinateUnityRead, isUnityStatusRead } from "./unityReadCoordinator";

const CALLABLE_REGION = "us-central1";
const CALLABLE_TIMEOUT_MS = 70000;
const ACCESS_VERIFICATION_TIMEOUT_MS = 20000;
const DASHBOARD_READ_TIMEOUT_MS = 30000;
const CALLABLE_ENDPOINT_UNAVAILABLE_REASON = "callable-endpoint-unavailable";

const CALLABLE_ERROR_CODES = Object.freeze({
  ABORTED: "aborted",
  ALREADY_EXISTS: "already-exists",
  CANCELLED: "cancelled",
  DATA_LOSS: "data-loss",
  DEADLINE_EXCEEDED: "deadline-exceeded",
  FAILED_PRECONDITION: "failed-precondition",
  INTERNAL: "internal",
  INVALID_ARGUMENT: "invalid-argument",
  NOT_FOUND: "not-found",
  OUT_OF_RANGE: "out-of-range",
  PERMISSION_DENIED: "permission-denied",
  RESOURCE_EXHAUSTED: "resource-exhausted",
  UNAUTHENTICATED: "unauthenticated",
  UNAVAILABLE: "unavailable",
  UNIMPLEMENTED: "unimplemented",
  UNKNOWN: "unknown",
});

const SAFE_ERROR_MESSAGES = Object.freeze({
  "functions/unauthenticated":
    "Your secure session could not be verified. Refresh the page and sign in again.",
  "functions/permission-denied":
    "You do not have permission to perform this action.",
  "functions/invalid-argument":
    "Please check the information you entered and try again.",
  "functions/not-found": "The requested record could not be found.",
  "functions/already-exists": "This record already exists.",
  "functions/failed-precondition":
    "This action cannot be completed in the current state.",
  "functions/resource-exhausted":
    "Too many attempts. Please wait before trying again.",
  "functions/deadline-exceeded": "The request took too long. Please try again.",
  "functions/unavailable":
    "The access service is temporarily unavailable. Please try again.",
  "functions/internal":
    "We could not verify access right now. Please try again.",
  "auth/email-already-in-use":
    "An account already exists for this email. Sign in instead.",
  "auth/invalid-credential": "The email or password is incorrect.",
  "auth/invalid-email": "Enter a valid work email address.",
  "auth/weak-password":
    "Choose a stronger password with at least 10 characters.",
  "auth/too-many-requests":
    "Too many attempts. Please wait before trying again.",
  "auth/network-request-failed":
    "We could not reach the service. Check your connection and try again.",
});

export function getSafeAccessError(
  error,
  fallback = "Something went wrong. Please try again.",
) {
  if (isPlatformAccessServiceUnavailable(error)) {
    return "This Platform administration service is not available yet. Please try again after the platform update is completed.";
  }
  const code = error?.code || error?.details?.code;
  const message = String(error?.message || "")
    .replace(/https?:\/\/[^\s]+/gi, "")
    .trim();
  const isFirebaseSafeMessage =
    message &&
    !/token|hash|stack|internal|permission|firebase|exception|credential/i.test(
      message,
    ) &&
    message.length <= 180;
  // Validation callables already return a precise, safe reason (e.g. which
  // field/combination was rejected); showing it beats a blanket generic line.
  // failed-precondition also carries app-specific reasons that are handled by
  // name at call sites (e.g. active_tenant_refresh_required); only fall
  // through to the raw message here when no such reason is present.
  if (
    (code === "functions/invalid-argument" ||
      (code === "functions/failed-precondition" && !error?.details?.reason)) &&
    isFirebaseSafeMessage
  ) {
    return message;
  }
  if (code && SAFE_ERROR_MESSAGES[code]) return SAFE_ERROR_MESSAGES[code];
  return isFirebaseSafeMessage ? message : fallback;
}

export function getSafeAiBillingError(
  error,
  fallback = "The AI wallet request could not be completed.",
) {
  if (isPlatformAccessServiceUnavailable(error)) {
    return (
      "The AI billing service has not been deployed yet. Deploy the " +
      "latest Cloud Functions and try again."
    );
  }
  const reason = error?.details?.reason;
  const reasonMessages = {
    invalid_money_amount:
      "Enter a USD amount with no more than two decimal places.",
    money_amount_out_of_range:
      "Enter an amount between USD 0.01 and USD 1,000,000.00.",
    invalid_model_rate:
      "Enter non-negative model rates with no more than six decimals.",
    model_rate_out_of_range:
      "A model rate cannot exceed USD 1,000,000 per million tokens.",
    invalid_model_updates: "Select at least one valid model cost to save.",
    invalid_margin: "Enter a valid markup percentage.",
    invalid_feature_key:
      "Choose a valid AI transaction from the server-provided catalog.",
    invalid_ai_transaction:
      "Choose a valid billable AI transaction from the server-provided catalog.",
    invalid_billing_month: "Choose a valid billing month.",
    ai_finance_report_incomplete:
      "The monthly finance report could not be reconciled to the authoritative wallet ledger. No partial report was downloaded.",
    ai_accounts_report_incomplete:
      "Your monthly AI Wallet account report could not be reconciled to the authoritative wallet ledger. No partial report was downloaded.",
    ai_accounts_snapshot_expired:
      "This account view has expired. Select Refresh account to load the latest activity, then try again.",
    ai_accounts_report_too_large:
      "This account view is too large to load. Refresh the account or contact platform support.",
    ai_finance_report_too_large:
      "This monthly finance report is too large for a direct download. No partial report was downloaded. Contact platform support for an assisted export.",
    invalid_migration_resolution:
      "Choose whether to discard the legacy balance or convert it to an approved USD amount.",
    missing_migration_amount:
      "Enter the administrator-approved USD conversion amount.",
    unexpected_migration_amount:
      "Do not enter a USD amount when discarding the legacy balance.",
    invalid_migration_amount:
      "Enter a valid administrator-approved USD conversion amount.",
    ai_wallet_migration_not_required:
      "This wallet migration has already been reconciled. Reload the tenant wallet.",
    active_tenant_refresh_required:
      "Your active tenant context changed. Refresh the page or sign in again " +
      "before taking any financial action.",
    idempotency_key_reused:
      "This request changed after submission. Reload the tenant wallet before retrying.",
    invalid_admission_attempt:
      "This billing review changed after it was loaded. Refresh the queue before retrying.",
    invalid_billing_resolution:
      "This billing recovery action is no longer supported. Refresh the queue.",
    ai_billing_review_not_releasable:
      "This billing item is no longer eligible for write-off. Refresh the queue.",
    ai_billing_review_not_stale:
      "This provider request is still active and cannot be written off yet. Refresh the queue after its availability time.",
    ai_wallet_overdrawn:
      "This tenant's AI Wallet is overdrawn. Top up to resume AI operations.",
    ai_wallet_top_up_unavailable:
      "Secure AI Wallet top-ups are temporarily unavailable for this tenant.",
    ai_wallet_top_up_configuration_required:
      "Secure AI Wallet top-ups are not configured yet. Contact platform support.",
    ai_wallet_top_up_offer_unavailable:
      "That funding option is no longer available. Reload the available options and try again.",
    "invalid-zar-amount":
      "Enter a valid rand amount with no more than two decimal places.",
    "zar-amount-too-small":
      "Enter a rand amount within the secure funding limits shown.",
    ai_wallet_fx_rate_unavailable:
      "An official exchange rate is not available right now. No payment was created; please try again later.",
    ai_wallet_fx_quote_invalid:
      "The official exchange-rate quote could not be verified. No payment was created; request a new quote.",
    ai_wallet_top_up_quote_invalid:
      "This funding quote could not be verified. Return and request a new quote.",
    ai_wallet_top_up_quote_expired:
      "This funding quote has expired. Return and request a new quote.",
    ai_wallet_top_up_quote_changed:
      "This funding quote changed before payment. Return and review a new quote.",
    ai_wallet_top_up_idempotency_conflict:
      "This payment retry no longer matches the original request. Start a new top-up.",
    ai_wallet_top_up_initializing:
      "Your secure payment is still being prepared. Wait a moment, then retry this step.",
    ai_wallet_top_up_provider_unavailable:
      "Callpay could not start this payment. No wallet credit was applied; please try again later.",
    ai_wallet_top_up_state_changed:
      "This payment changed while it was being prepared. Check its status before trying again.",
    ai_billing_hold:
      "This AI transaction is awaiting reconciliation. Continue its saved request to check recovery; available funds can be used for other AI operations.",
    ai_wallet_billing_hold:
      "This AI transaction is awaiting reconciliation. Continue its saved request to check recovery; available funds can be used for other AI operations.",
    ai_billing_state_persistence_failed:
      "This AI transaction could not be saved. Continue its saved request to check recovery. Completed work and other wallet transactions are retained.",
    ai_image_pricing_configuration_required:
      "Unity could not verify its built-in image pricing. No charge was " +
      "applied; retry or contact support.",
    invalid_rate_card:
      "The rate card contains invalid or incomplete model pricing.",
    invalid_rate_card_file:
      "Choose the approved OpenAI rate-card workbook (.xlsx).",
  };
  if (reason && reasonMessages[reason]) return reasonMessages[reason];

  const code = error?.code || error?.details?.code;
  const message = String(error?.message || "").trim();
  const safeServerValidation =
    code === "functions/invalid-argument" &&
    message.length > 0 &&
    message.length <= 240 &&
    !/stack|hash|firebase|exception|credential|bearer|app.?check/i.test(
      message,
    );
  if (safeServerValidation) return message;
  return getSafeAccessError(error, fallback);
}

export function isPlatformAccessServiceUnavailable(error) {
  return (
    error?.code === "functions/not-found" &&
    error?.details?.reason === CALLABLE_ENDPOINT_UNAVAILABLE_REASON
  );
}

function callableUrl(name) {
  const projectId = String(auth?.app?.options?.projectId || "").trim();
  const configuredOrigin = String(
    process.env.REACT_APP_FIREBASE_FUNCTIONS_ORIGIN || "",
  )
    .trim()
    .replace(/\/$/, "");
  if (configuredOrigin) {
    const localOrigin =
      /^https?:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?$/i.test(
        configuredOrigin,
      );
    return localOrigin
      ? `${configuredOrigin}/${projectId}/${CALLABLE_REGION}/${name}`
      : `${configuredOrigin}/${name}`;
  }
  return `https://${CALLABLE_REGION}-${projectId}.cloudfunctions.net/${name}`;
}

function applicationCallableError(callableResult) {
  const appError = callableResult?.error || {};
  const message = String(appError.message || "").trim() || "The request failed.";
  const isQuotaError =
    /429|no credits remaining|insufficient[_ ]quota|add credits to continue/i.test(
      message,
    ) ||
    /429|insufficient_quota|credits/i.test(String(appError.code || ""));
  return new FunctionsError(
    isQuotaError ? "resource-exhausted" : "failed-precondition",
    message,
    {
      ...appError,
      data: callableResult.data,
      correlationId: callableResult.correlationId,
    },
  );
}

function callableError(response, statusCode, callableName, retryAfter) {
  const status = String(response?.error?.status || "")
    .trim()
    .toUpperCase();
  const endpointUnavailable = statusCode === 404 && !response?.error;
  const code =
    CALLABLE_ERROR_CODES[status] ||
    (statusCode === 401
      ? "unauthenticated"
      : statusCode === 403
        ? "permission-denied"
        : statusCode === 404
          ? "not-found"
          : statusCode === 429
            ? "resource-exhausted"
            : statusCode >= 500
              ? "internal"
              : "unknown");
  return new FunctionsError(
    code,
    endpointUnavailable
      ? "The Platform administration service is not available."
      : typeof response?.error?.message === "string"
        ? response.error.message
        : "The access service request failed.",
    endpointUnavailable
      ? {
          reason: CALLABLE_ENDPOINT_UNAVAILABLE_REASON,
          callableName,
        }
      : code === "resource-exhausted" && retryAfter
        ? {
            ...response?.error?.details,
            retryAfterSeconds: Math.max(
              Number(response?.error?.details?.retryAfterSeconds) || 0,
              Number.isFinite(Number(retryAfter))
                ? Number(retryAfter)
                : Math.max(
                    0,
                    Math.ceil((Date.parse(retryAfter) - Date.now()) / 1000),
                  ) || 0,
            ),
          }
        : response?.error?.details,
  );
}

function awaitWithAbort(promise, signal) {
  return new Promise((resolve, reject) => {
    const rejectForAbort = () => {
      cleanup();
      const error = new Error("The access service request timed out.");
      error.name = "AbortError";
      reject(error);
    };
    const cleanup = () => signal.removeEventListener("abort", rejectForAbort);

    if (signal.aborted) {
      rejectForAbort();
      return;
    }

    signal.addEventListener("abort", rejectForAbort, { once: true });
    Promise.resolve(promise).then(
      (value) => {
        cleanup();
        resolve(value);
      },
      (error) => {
        cleanup();
        reject(error);
      },
    );
  });
}

// Share only a currently running refresh, never a refreshed token or response.
const unityReadTokenRefreshes = new WeakMap();

function unityReadAuthorizationScope(token, uid) {
  try {
    const parts = token.split(".");
    if (
      parts.length !== 3 ||
      parts[1].length > 32768 ||
      !/^[A-Za-z0-9_-]+$/.test(parts[1])
    )
      return null;
    const encoded = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const bytes = atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, "="));
    const claims = JSON.parse(
      decodeURIComponent(
        Array.from(
          bytes,
          (char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`,
        ).join(""),
      ),
    );
    if (
      !claims ||
      Array.isArray(claims) ||
      claims.sub !== uid ||
      typeof claims.aud !== "string" ||
      typeof claims.iss !== "string"
    )
      return null;
    const stable = (value) =>
      value && typeof value === "object"
        ? Array.isArray(value)
          ? value.map(stable)
          : Object.fromEntries(
              Object.keys(value)
                .sort()
                .map((key) => [key, stable(value[key])]),
            )
        : value;
    // Parsing is only a refusal fence, not authentication. The server still
    // verifies signatures/claims. Only token issuance/expiry may change: all
    // tenant, membership, auth_time and Firebase identity claims must match.
    const { exp, iat, ...authorization } = claims;
    return JSON.stringify(stable(authorization));
  } catch (_error) {
    return null;
  }
}

function assertCurrentReadSession(user, uid, signal) {
  if (signal.aborted) {
    const error = new Error("The access service request timed out.");
    error.name = "AbortError";
    throw error;
  }
  if (auth.currentUser !== user || user?.uid !== uid) {
    throw new FunctionsError(
      "unauthenticated",
      "Your secure session changed. Refresh the page and sign in again.",
    );
  }
}

function refreshUnityReadToken(user) {
  const existing = unityReadTokenRefreshes.get(user);
  if (existing) return existing;
  const refresh = Promise.resolve(user.getIdToken(true));
  unityReadTokenRefreshes.set(user, refresh);
  const clear = () => {
    if (unityReadTokenRefreshes.get(user) === refresh) {
      unityReadTokenRefreshes.delete(user);
    }
  };
  refresh.then(clear, clear);
  return refresh;
}

export async function callPlatformAccess(name, payload = {}, options = {}) {
  const controller = new AbortController();
  const requestedTimeoutMs = Number(options.timeoutMs);
  const minimumTimeoutMs =
    options.allowShortTimeout === true ? 5000 : CALLABLE_TIMEOUT_MS;
  const timeoutMs = Number.isFinite(requestedTimeoutMs)
    ? Math.min(600000, Math.max(minimumTimeoutMs, requestedTimeoutMs))
    : CALLABLE_TIMEOUT_MS;
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const requestUser = auth.currentUser;
    const requestUid = requestUser?.uid;
    const statusRead = isUnityStatusRead(name);
    const assertReadSession = () => {
      if (statusRead) {
        assertCurrentReadSession(requestUser, requestUid, controller.signal);
      }
    };
    const [idToken, appCheckToken] = await awaitWithAbort(
      Promise.all([
        requestUser ? requestUser.getIdToken() : Promise.resolve(""),
        getCentralAccessAppCheckToken(),
      ]),
      controller.signal,
    );
    assertReadSession();
    const headers = { "Content-Type": "application/json" };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (appCheckToken) headers["X-Firebase-AppCheck"] = appCheckToken;

    const url = callableUrl(name);
    const invokeWithToken = (activeToken, mayRefresh) =>
      coordinateUnityRead({
        user: requestUser,
        idToken: activeToken,
        url,
        name,
        payload,
        authRetry: !mayRefresh,
        invoke: async () => {
          assertReadSession();
          const networkResponse = await fetch(url, {
            method: "POST",
            headers: activeToken
              ? {
                  ...headers,
                  Authorization: `Bearer ${activeToken}`,
                }
              : headers,
            body: JSON.stringify({ data: payload }),
            signal: controller.signal,
          });
          const response = await networkResponse.json().catch(() => null);
          assertReadSession();
          if (!networkResponse.ok || response?.error) {
            const error = callableError(
              response,
              networkResponse.status,
              name,
              networkResponse.headers?.get?.("Retry-After"),
            );
            // Only an actual rejected HTTP read can be replayed. Never infer
            // expiry from an application message or replay generation commands.
            if (
              mayRefresh &&
              statusRead &&
              requestUid &&
              activeToken &&
              networkResponse.status === 401 &&
              response?.error?.status === "UNAUTHENTICATED"
            ) {
              const previousScope = unityReadAuthorizationScope(
                activeToken,
                requestUid,
              );
              if (!previousScope) throw error;
              let refreshedToken;
              try {
                refreshedToken = await awaitWithAbort(
                  refreshUnityReadToken(requestUser),
                  controller.signal,
                );
              } catch (refreshError) {
                assertReadSession();
                throw error;
              }
              assertReadSession();
              if (
                typeof refreshedToken !== "string" ||
                !refreshedToken ||
                refreshedToken === activeToken
              )
                throw error;
              if (
                unityReadAuthorizationScope(refreshedToken, requestUid) !==
                previousScope
              ) {
                throw error;
              }
              return invokeWithToken(refreshedToken, false);
            }
            throw error;
          }
          const callableResult = response?.data ?? response?.result;
          if (callableResult === undefined) {
            throw new FunctionsError(
              "internal",
              "The access service returned an invalid response.",
            );
          }

          if (callableResult && callableResult.ok === false) {
            throw applicationCallableError(callableResult);
          }

          if (!callableResult || callableResult.ok !== true) {
            throw new FunctionsError(
              "internal",
              "The access service returned an invalid response.",
            );
          }
          return callableResult.data;
        },
      });
    const result = await awaitWithAbort(
      invokeWithToken(idToken, true),
      controller.signal,
    );
    // A coalesced read must also recheck this caller's current session.
    assertReadSession();
    if (auth.currentUser === requestUser) notifyAccessMutation(name, payload, requestUser);
    return result;
  } catch (error) {
    if (error?.name === "AbortError") {
      throw new FunctionsError(
        "deadline-exceeded",
        "The access service request timed out.",
      );
    }
    if (error instanceof FunctionsError) throw error;
    throw new FunctionsError(
      "unavailable",
      "The access service could not be reached.",
    );
  } finally {
    clearTimeout(timeout);
  }
}

// Read-only dashboard calls are commonly replayed by React StrictMode or
// requested by two mounted views at the same time. Share only the active
// promise: once it settles, the next caller always performs a fresh read.
const concurrentReads = new Map();

function coalescedRead(key, name, payload = {}, options = {}) {
  const userScope = String(auth?.currentUser?.uid || "anonymous");
  const scopedKey = `${userScope}:${key}`;
  const existing = concurrentReads.get(scopedKey);
  if (existing) return existing;

  const request = callPlatformAccess(name, payload, {
    allowShortTimeout: true,
    timeoutMs: DASHBOARD_READ_TIMEOUT_MS,
    ...options,
  });
  concurrentReads.set(scopedKey, request);
  request
    .finally(() => {
      if (concurrentReads.get(scopedKey) === request) {
        concurrentReads.delete(scopedKey);
      }
    })
    .catch(() => {
      // The original request remains responsible for reporting its rejection.
    });
  return request;
}

export const platformAccessApi = {
  getAccessContext: () =>
    callPlatformAccess(
      "getAccessContext",
      {},
      { allowShortTimeout: true, timeoutMs: ACCESS_VERIFICATION_TIMEOUT_MS },
    ),
  setActiveTenant: async (tenantId) => {
    // Never let an in-flight read from the previous tenant be reused after a
    // context switch. Existing callers may finish safely, while subsequent
    // views start a fresh server-authoritative read.
    concurrentReads.clear();
    try {
      return await callPlatformAccess("setActiveTenant", { tenantId });
    } finally {
      concurrentReads.clear();
    }
  },

  requestPlatformAdminRegistrationCode: (displayName) =>
    callPlatformAccess("requestPlatformAdminRegistrationCode", { displayName }),
  resendPlatformAdminRegistrationCode: (requestId) =>
    callPlatformAccess("resendPlatformAdminRegistrationCode", { requestId }),
  completePlatformAdminRegistration: (requestId, code) =>
    callPlatformAccess("completePlatformAdminRegistration", {
      requestId,
      code,
    }),
  getPlatformAdminRegistrationStatus: (requestId) =>
    callPlatformAccess("getPlatformAdminRegistrationStatus", { requestId }),
  requestPlatformAdminInvitationApproval: (input) =>
    callPlatformAccess("requestPlatformAdminInvitationApproval", input),
  approveAndSendPlatformAdminInvitation: (approvalId, code) =>
    callPlatformAccess("approveAndSendPlatformAdminInvitation", {
      approvalId,
      ...(code ? { code } : {}),
    }),
  listPlatformAdminInvitations: (filters = {}) =>
    callPlatformAccess("listPlatformAdminInvitations", filters),
  revokePlatformAdminInvitation: (inviteId) =>
    callPlatformAccess("revokePlatformAdminInvitation", { inviteId }),
  validatePlatformAdminInvitation: (token, candidateEmail) =>
    callPlatformAccess("validatePlatformAdminInvitation", {
      token,
      ...(candidateEmail ? { candidateEmail } : {}),
    }),
  completePlatformAdminInvitation: (token, displayName) =>
    callPlatformAccess("completePlatformAdminInvitation", {
      token,
      displayName,
    }),

  bootstrapFleetCoreAccess: () =>
    callPlatformAccess("bootstrapFleetCoreAccess"),

  validateTenantAdminInvitation: (token, candidateEmail) =>
    callPlatformAccess("validateTenantAdminInvitation", {
      token,
      ...(candidateEmail ? { candidateEmail } : {}),
    }),
  completeTenantAdminRegistration: (token, displayName) =>
    callPlatformAccess("completeTenantAdminRegistration", {
      token,
      displayName,
    }),
  validateTenantUserInvitation: (token, candidateEmail) =>
    callPlatformAccess("validateTenantUserInvitation", {
      token,
      ...(candidateEmail ? { candidateEmail } : {}),
    }),
  completeTenantUserRegistration: (token, displayName, learnerProfile) =>
    callPlatformAccess("completeTenantUserRegistration", {
      token,
      displayName,
      ...(learnerProfile ? { learnerProfile } : {}),
    }),

  getPlatformAdminDashboard: () =>
    callPlatformAccess("getPlatformAdminDashboard"),
  listTenants: (filters = {}) => callPlatformAccess("listTenants", filters),
  getTenantForPlatformAdmin: (tenantId) =>
    callPlatformAccess("getTenantForPlatformAdmin", { tenantId }),
  createTenant: (input) => callPlatformAccess("createTenant", input),
  updateTenantProfile: (input) =>
    callPlatformAccess("updateTenantProfile", input),
  uploadTenantLogo: (input) => callPlatformAccess("uploadTenantLogo", input),
  removeTenantLogo: (tenantId) =>
    callPlatformAccess("removeTenantLogo", { tenantId }),
  getDocumentTheme: (tenantId) =>
    callPlatformAccess("getDocumentTheme", tenantId ? { tenantId } : {}),
  listDocumentThemeDocuments: (tenantId) =>
    callPlatformAccess("listDocumentThemeDocuments", tenantId ? { tenantId } : {}),
  recordDocumentThemeDocument: (input) =>
    callPlatformAccess("recordDocumentThemeDocument", input),
  saveDocumentThemeDraft: (input) =>
    callPlatformAccess("saveDocumentThemeDraft", input),
  manageDocumentThemePreset: (input) =>
    callPlatformAccess("manageDocumentThemePreset", input),
  publishDocumentTheme: (input) =>
    callPlatformAccess("publishDocumentTheme", input),
  resetDocumentThemeDraft: (input) =>
    callPlatformAccess("resetDocumentThemeDraft", input),
  restoreDocumentThemeVersion: (input) =>
    callPlatformAccess("restoreDocumentThemeVersion", input),
  uploadDocumentThemeCoverImage: (input) =>
    callPlatformAccess("uploadDocumentThemeCoverImage", input),
  uploadDocumentThemeWatermarkImage: (input) =>
    callPlatformAccess("uploadDocumentThemeWatermarkImage", input),
  uploadDocumentThemeRunningImage: (input) =>
    callPlatformAccess("uploadDocumentThemeRunningImage", input),
  uploadDocumentThemeIconImage: (input) =>
    callPlatformAccess("uploadDocumentThemeIconImage", input),
  removeUnusedDocumentThemeImage: (input) =>
    callPlatformAccess("removeUnusedDocumentThemeImage", input),
  setTenantSystemAccess: (input) =>
    callPlatformAccess("setTenantSystemAccess", input),
  setTenantStatus: (tenantId, status) =>
    callPlatformAccess("setTenantStatus", { tenantId, status }),
  createTenantAdminInvitation: (input) =>
    callPlatformAccess("createTenantAdminInvitation", input),
  resendTenantAdminInvitation: (inviteId) =>
    callPlatformAccess("resendTenantAdminInvitation", { inviteId }),
  revokeTenantAdminInvitation: (inviteId) =>
    callPlatformAccess("revokeTenantAdminInvitation", { inviteId }),
  setTenantAdminStatus: (input) =>
    callPlatformAccess("setTenantAdminStatus", input),
  setTenantAdminRoles: (input) =>
    callPlatformAccess("setTenantAdminRoles", input),
  listPlatformAdmins: (filters = {}) =>
    callPlatformAccess("listPlatformAdmins", filters),
  setPlatformAdminStatus: (uid, status) =>
    callPlatformAccess("setPlatformAdminStatus", { uid, status }),
  setPlatformAdminRole: (uid, platformRole) =>
    callPlatformAccess("setPlatformAdminRole", { uid, platformRole }),
  listPlatformAuditEvents: (filters = {}) =>
    callPlatformAccess("listPlatformAuditEvents", filters),
  getAiBillingConfiguration: () =>
    callPlatformAccess("getAiBillingConfiguration"),
  updateGlobalAiBillingMargin: (input) =>
    callPlatformAccess("updateGlobalAiBillingMargin", input),
  updateGlobalAiRateCard: (input) =>
    callPlatformAccess("updateGlobalAiRateCard", input),
  importGlobalAiRateCard: (input) =>
    callPlatformAccess("importGlobalAiRateCard", input),
  getTenantAiWallet: (tenantId) =>
    callPlatformAccess("getTenantAiWallet", { tenantId }),
  fundTenantAiWallet: (input) =>
    callPlatformAccess("platformFundTenantAiWallet", input),
  reconcileTenantAiWalletMigration: (input) =>
    callPlatformAccess("reconcileTenantAiWalletMigration", input),
  updateTenantAiMargin: (input) =>
    callPlatformAccess("updateTenantAiMargin", input),
  getTenantAiTransactionCatalog: (tenantId) =>
    callPlatformAccess("getTenantAiTransactionCatalog", { tenantId }),
  updateTenantAiTransactionMargin: (input) =>
    callPlatformAccess("updateTenantAiTransactionMargin", input),
  getTenantAiWalletActivity: (input) =>
    callPlatformAccess("getTenantAiWalletActivity", input),
  getTenantAiWalletFinanceReport: (input) =>
    callPlatformAccess("getTenantAiWalletFinanceReport", input, {
      timeoutMs: 115000,
    }),
  getTenantAiBillingReviewQueue: (input) =>
    callPlatformAccess("getTenantAiBillingReviewQueue", input),
  resolveTenantAiBillingReview: (input) =>
    callPlatformAccess("resolveTenantAiBillingReview", input),
  // Temporary aliases keep already-mounted clients safe during the wallet
  // terminology rollout. They use the new dollar-authoritative endpoints.
  getTenantAiCreditAccount: (tenantId) =>
    callPlatformAccess("getTenantAiWallet", { tenantId }),

  getTenantAdminDashboard: () =>
    coalescedRead("getTenantAdminDashboard", "getTenantAdminDashboard"),
  listTenantUsers: (filters = {}) =>
    callPlatformAccess("listTenantUsers", filters),
  createTenantUserInvitation: (input) =>
    callPlatformAccess("createTenantUserInvitation", input),
  resendTenantUserInvitation: (inviteId) =>
    callPlatformAccess("resendTenantUserInvitation", { inviteId }),
  revokeTenantUserInvitation: (inviteId) =>
    callPlatformAccess("revokeTenantUserInvitation", { inviteId }),
  setTenantUserStatus: (uid, status) =>
    callPlatformAccess("setTenantUserStatus", { uid, status }),
  setTenantUserRoles: (uid, systemRoles) =>
    callPlatformAccess("setTenantUserRoles", { uid, systemRoles }),
  listPreAuditSuppliers: () =>
    callPlatformAccess("preAuditListSuppliers"),
  listPreAuditSupplierRepresentatives: (supplierId) =>
    callPlatformAccess("preAuditListSupplierRepresentatives", { supplierId }),
  invitePreAuditSupplierRepresentative: (input) =>
    callPlatformAccess("preAuditInviteSupplierRepresentative", input),
  listTenantAuditEvents: (filters = {}) =>
    callPlatformAccess("listTenantAuditEvents", filters),
  getTenantAiWalletSummary: (tenantScope = "") =>
    coalescedRead(
      `getTenantAiWalletSummary:${String(tenantScope || "")}`,
      "getTenantAiWalletSummary",
    ),
  listMyTenantAiWalletTopUpOffers: () =>
    callPlatformAccess("listMyTenantAiWalletTopUpOffers"),
  quoteMyTenantAiWalletTopUp: (input) =>
    callPlatformAccess("quoteMyTenantAiWalletTopUp", input),
  createMyTenantAiWalletTopUp: (input) =>
    callPlatformAccess("createMyTenantAiWalletTopUp", input),
  getMyTenantAiWalletTopUp: (input) =>
    callPlatformAccess("getMyTenantAiWalletTopUp", input),
  getMyTenantAiWalletActivity: (input = {}) =>
    callPlatformAccess("getMyTenantAiWalletActivity", input),
  getMyTenantAiWalletAccountReport: (input = {}, tenantScope = "") =>
    coalescedRead(
      `getMyTenantAiWalletAccountReport:${String(tenantScope || "")}:` +
        JSON.stringify([
          input?.month || "", input?.actionId || "", input?.pageSize || "",
          input?.pageToken || "", input?.snapshotAt || "",
        ]),
      "getMyTenantAiWalletAccountReport",
      input,
      { timeoutMs: 115000 },
    ),
  getTenantAiCreditSummary: (tenantScope = "") =>
    coalescedRead(
      `getTenantAiWalletSummary:${String(tenantScope || "")}`,
      "getTenantAiWalletSummary",
    ),
  getProductLaunchContext: (productKey, moduleKey) =>
    coalescedRead(
      `getProductLaunchContext:${String(productKey || "")}:${String(moduleKey || "")}`,
      "getProductLaunchContext",
      { productKey, ...(moduleKey ? { module: moduleKey } : {}) },
    ),
};
